import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { tokenStore, authHeader } from "@/lib/token";
import { getDeviceId } from "@/lib/deviceId";
import { LanguageProvider } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import CheckoutPage from "@/pages/CheckoutPage";
import SuccessPage from "@/pages/SuccessPage";
import BotDetailPage from "@/pages/BotDetailPage";
import BotManagementPage from "@/pages/BotManagementPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import SupportPage from "@/pages/SupportPage";
import TutorialPage from "@/pages/TutorialPage";
import VantaPhoneDocs from "@/pages/VantaPhoneDocs";
import AntiCheatPage from "@/pages/AntiCheatPage";
import BotDashboardPage from "@/pages/BotDashboardPage";
import BotConsolePage from "@/pages/BotConsolePage";
import ComparePage from "@/pages/ComparePage";
import VantaAIPage from "@/pages/VantaAIPage";
import { LegalNotice, SalesTerms, RefundPolicy } from "@/pages/LegalPages";
import ShopPage from "@/pages/ShopPage";
import SettingsPage from "@/pages/SettingsPage";
import Onboarding from "@/pages/Onboarding";
import PasswordLogin from "@/pages/PasswordLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import StatusPage from "@/pages/StatusPage";
import RecruitmentPage from "@/pages/RecruitmentPage";
import AmbassadorPage from "@/pages/AmbassadorPage";
import NotFound from "@/pages/NotFound";
import MaintenancePage from "@/pages/MaintenancePage";
import SeasonalLayer from "@/components/SeasonalLayer";
import ParticlesBg from "@/components/ParticlesBg";
import SupportAIWidget from "@/components/SupportAIWidget";

// En ligne, l'API est appelée sur l'adresse du site elle-même (vanta-pulse.shop ou
// l'adresse Vercel) : Vercel la relaie au backend (voir frontend/vercel.json).
// En local (localhost), on garde REACT_APP_BACKEND_URL.
const IS_LOCAL = typeof window === 'undefined' || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const BACKEND_URL = IS_LOCAL ? (process.env.REACT_APP_BACKEND_URL || 'https://vanta-pulse.shop') : window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Envoyé sur toutes les requêtes (axios est un module singleton) : permet au backend
// de reconnaître le même appareil d'une connexion à l'autre même quand l'IP change.
axios.defaults.headers.common["X-Device-Id"] = getDeviceId();

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Protected Route Component
// N'est utilisé que pour /password-login : à ce stade, `user` n'est volontairement pas
// encore renseigné pour une session OAuth pas encore vérifiée (le jeton reçu est limité
// à /auth/verify-password, voir create_oauth_pending_token côté backend). On se base donc
// sur la présence d'un jeton (peu importe lequel) plutôt que sur `user`.
const ProtectedRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-vanta-base flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!tokenStore.get()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Protected Route with Password Verification
const ProtectedRouteWithPassword = ({ children }) => {
  const { user, loading, passwordVerified } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-vanta-base flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (!passwordVerified) {
    return <Navigate to="/password-login" replace />;
  }
  
  return children;
};

// Router wrapper to detect OAuth callback
function AppRouter({ siteStatus }) {
  const location = useLocation();
  const { user } = useAuth();
  // Titre, description et adresse canonique de la page (référencement).
  useSeo(location.pathname);

  // CRITICAL: Check URL fragment synchronously for session_id (Google OAuth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check for Google OAuth callback with code in URL params
  if (location.search?.includes('code=')) {
    return <AuthCallback />;
  }

  // Mode maintenance : bloque tout le site sauf pour un admin, qui doit pouvoir s'y
  // connecter pour la désactiver depuis le panel admin.
  if (siteStatus?.maintenance_mode && !user?.is_admin) {
    return <MaintenancePage siteStatus={siteStatus} />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/bot/:botType" element={<BotDetailPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/tutorial" element={<TutorialPage />} />
        <Route path="/shop" element={<ShopPage />} />
        {/* Vanta AI : l'assistant IA (conversations, offres) */}
        <Route path="/ai" element={<VantaAIPage />} />
        <Route path="/ai/chat/:chatId" element={<VantaAIPage />} />
        <Route path="/ai/upgrade" element={<VantaAIPage />} />
        <Route path="/ai/api" element={<VantaAIPage />} />
        <Route path="/ai/artifacts" element={<VantaAIPage />} />
        <Route path="/ai/apps" element={<VantaAIPage />} />
        <Route path="/comparer" element={<ComparePage />} />
        {/* ancienne page Partenariats (retirée) : on renvoie vers l'accueil */}
        <Route path="/partenaires" element={<Navigate to="/" replace />} />
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/cgv" element={<SalesTerms />} />
        <Route path="/remboursement" element={<RefundPolicy />} />
        <Route path="/settings" element={<ProtectedRouteWithPassword><SettingsPage /></ProtectedRouteWithPassword>} />
        {/* Onboarding handles its own guard: either a logged-in user who still needs a
            password, or a pending OAuth/email signup with no session yet at all. */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/password-login" element={<ProtectedRoute><PasswordLogin /></ProtectedRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
        <Route path="/conditions-utilisation" element={<TermsOfService />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/recrutement" element={<RecruitmentPage />} />
        <Route path="/bot-manager" element={<ProtectedRouteWithPassword><BotManagementPage /></ProtectedRouteWithPassword>} />
        <Route path="/bot-manager/:botId" element={<ProtectedRouteWithPassword><BotManagementPage /></ProtectedRouteWithPassword>} />
        <Route path="/admin" element={<ProtectedRouteWithPassword><AdminDashboard /></ProtectedRouteWithPassword>} />
        <Route path="/dashboard" element={<ProtectedRouteWithPassword><Dashboard /></ProtectedRouteWithPassword>} />
        <Route path="/ambassadeur" element={<ProtectedRouteWithPassword><AmbassadorPage /></ProtectedRouteWithPassword>} />
        <Route path="/docs/vantaphone" element={<ProtectedRouteWithPassword><VantaPhoneDocs /></ProtectedRouteWithPassword>} />
        <Route path="/anticheat/:botId" element={<ProtectedRouteWithPassword><AntiCheatPage /></ProtectedRouteWithPassword>} />
        <Route path="/bot-dashboard" element={<ProtectedRouteWithPassword><BotDashboardPage /></ProtectedRouteWithPassword>} />
        <Route path="/bot-dashboard/:botId" element={<ProtectedRouteWithPassword><BotDashboardPage /></ProtectedRouteWithPassword>} />
        <Route path="/bot-dashboard/:botId/:guildId" element={<ProtectedRouteWithPassword><BotDashboardPage /></ProtectedRouteWithPassword>} />
        <Route path="/bot-console/:botId" element={<ProtectedRouteWithPassword><BotConsolePage /></ProtectedRouteWithPassword>} />
        <Route path="/checkout" element={<ProtectedRouteWithPassword><CheckoutPage /></ProtectedRouteWithPassword>} />
        <Route path="/success" element={<ProtectedRouteWithPassword><SuccessPage /></ProtectedRouteWithPassword>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!location.pathname.startsWith("/admin") && !location.pathname.startsWith("/ai") && <SupportAIWidget />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [siteStatus, setSiteStatus] = useState(null);

  useEffect(() => {
    let active = true;
    const loadSiteStatus = () => {
      axios.get(`${API}/site-status`).then((res) => { if (active) setSiteStatus(res.data); }).catch(() => {});
    };
    loadSiteStatus();
    const interval = setInterval(loadSiteStatus, 30000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile/me`, { headers: authHeader() });
      setProfile(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user) refreshProfile();
    else setProfile(null);
  }, [user, refreshProfile]);

  const login = useCallback((token, userData) => {
    tokenStore.set(token);
    setUser(userData);
    // Store last activity timestamp
    localStorage.setItem('vantapulse_last_activity', Date.now().toString());
    setPasswordVerified(true);
  }, []);

  // Used after an OAuth login (Google/Discord) for an account that already exists:
  // stores the session but does NOT mark the password as verified, so the user is
  // routed through /password-login (and 2FA, if enabled) before gaining access.
  const beginOAuthSession = useCallback((token, userData) => {
    tokenStore.set(token);
    setUser(userData);
    setPasswordVerified(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    tokenStore.remove();
    setUser(null);
    setPasswordVerified(false);
    localStorage.removeItem('vantapulse_last_activity');
  }, []);

  // Update last activity on user interactions
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      localStorage.setItem('vantapulse_last_activity', Date.now().toString());
    };

    // Update activity on various user interactions
    const events = ['click', 'keydown', 'scroll', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user]);

  // Capture un éventuel code de parrainage (?ref=CODE) présent sur n'importe quelle
  // page, et le conserve jusqu'à l'inscription (utilisé par AuthModal/Onboarding).
  // Lien d'un ambassadeur : la visite est comptée (une fois par jour et par
  // navigateur, avec un identifiant aléatoire).
  useEffect(() => {
    const refCode = new URLSearchParams(window.location.search).get("ref");
    if (refCode) {
      try { localStorage.setItem("vantapulse_ref_code", refCode.toUpperCase()); } catch { /* ignore */ }
      let visitor = "";
      try {
        visitor = localStorage.getItem("vantapulse_visitor") || "";
        if (!visitor) { visitor = `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("vantapulse_visitor", visitor); }
      } catch { visitor = `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; }
      axios.post(`${API}/ref/click`, { code: refCode.slice(0, 40), visitor }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // Erreur renvoyée par la connexion Google/Discord : on l'affiche au lieu de
    // revenir silencieusement sur l'accueil.
    const authError = params.get("auth_error");
    if (authError) {
      const AUTH_ERRORS = {
        email_unverified: "Votre adresse email n'est pas vérifiée sur ce compte (Discord/Google). Vérifiez-la puis réessayez.",
        banned: "Ce compte a été banni.",
        discord_denied: "Connexion Discord annulée.",
        google_cancelled: "Connexion Google annulée.",
        google_failed: "Google n'a pas validé la connexion (lien expiré ou déjà utilisé). Réessayez.",
        google_unreachable: "Google ne répond pas pour le moment. Réessayez dans un instant.",
        google_no_email: "Google n'a pas donné d'adresse email pour ce compte.",
        google_not_configured: "La connexion Google n'est pas disponible pour le moment. Utilisez votre email ou Discord.",
      };
      toast.error(AUTH_ERRORS[authError] || "La connexion n'a pas pu aboutir, réessayez.");
      params.delete("auth_error");
      const rest = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }

    // Discord OAuth for an account that doesn't exist yet: nothing has been saved
    // server-side. Stash the pending signup and let Onboarding create the account
    // once the password is validated.
    const pendingToken = params.get("pending_token");
    if (pendingToken) {
      window.history.replaceState(null, "", "/");
      sessionStorage.setItem("pending_signup", JSON.stringify({ pending_token: pendingToken }));
      window.location.assign("/onboarding");
      return;
    }

    // Accès "Regarder" ouvert par un admin depuis le panel : connexion immédiate sur
    // le compte ciblé, sans mot de passe ni A2F (jeton déjà vérifié côté serveur).
    const impersonateToken = params.get("impersonate_token");
    if (impersonateToken) {
      tokenStore.set(impersonateToken);
      window.history.replaceState(null, "", "/");
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${impersonateToken}` }
      })
      .then(res => {
        setUser(res.data);
        setPasswordVerified(true);
        localStorage.setItem('vantapulse_last_activity', Date.now().toString());
        window.location.assign("/dashboard");
      })
      .catch(() => {
        tokenStore.remove();
      })
      .finally(() => {
        setLoading(false);
      });
      return;
    }

    const discordToken = params.get("token");
    if (discordToken) {
      const needsPasswordParam = params.get("needs_password");
      const needsPasswordSetup = needsPasswordParam === "1";
      // "2" : pas de mot de passe VantaPulse sur ce compte, mais l'A2F est activée.
      // Il faut quand même passer par la connexion par code + A2F sur /password-login
      // avant d'accorder l'accès, sinon l'OAuth contournerait l'A2F entièrement.
      const needsCodeThenPasswordSetup = needsPasswordParam === "2";
      tokenStore.set(discordToken);
      window.history.replaceState(null, "", "/");
      setPasswordVerified(false);

      if (needsPasswordSetup) {
        // Compte hérité sans mot de passe VantaPulse : ce jeton donne un accès complet
        // (rien à protéger tant qu'aucun mot de passe n'existe), direction onboarding
        // pour en définir un.
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${discordToken}` }
        })
        .then(res => {
          setUser(res.data);
          window.location.assign("/onboarding");
        })
        .catch(() => {
          tokenStore.remove();
        })
        .finally(() => {
          setLoading(false);
        });
      } else {
        // Compte déjà protégé par un mot de passe, ou par l'A2F sans mot de passe : le
        // jeton reçu est volontairement limité (voir create_oauth_pending_token côté
        // backend) — pas d'appeler /auth/me. `user` ne sera renseigné qu'une fois cette
        // étape validée sur /password-login.
        if (needsCodeThenPasswordSetup) {
          sessionStorage.setItem("oauth_needs_password_setup_after", "1");
        }
        window.location.assign("/password-login");
        setLoading(false);
      }
      return;
    }

    const token = tokenStore.get();
    if (token) {
      if (window.location.pathname === "/password-login") {
        // Le jeton stocké ici peut être un jeton OAuth volontairement limité (voir
        // create_oauth_pending_token côté backend) : /auth/me le rejette par design,
        // ce qui ferait supprimer le jeton avant même d'arriver sur l'écran mot de
        // passe. PasswordLogin gère lui-même la validité du jeton via
        // /auth/verify-password, donc on ne le valide pas ici.
        setLoading(false);
        return;
      }
      // Sur mobile, revenir sur l'onglet après une mise en veille s'accompagne souvent
      // d'une reconnexion réseau pas encore terminée : cette requête peut échouer sans
      // que le jeton soit invalide pour autant. On ne déconnecte donc l'utilisateur que
      // si le serveur rejette explicitement le jeton (401/403) ; une erreur réseau est
      // réessayée quelques fois avant d'abandonner, sans jamais supprimer le jeton.
      const tryFetchMe = (attemptsLeft) => {
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
          setUser(res.data);
          // Reste connecté tant que la dernière activité est plus récente que la durée
          // choisie par l'utilisateur (Paramètres > Préférences, 30 jours par défaut).
          const lastActivity = localStorage.getItem('vantapulse_last_activity');
          const sessionDurationDays = res.data.session_duration_days || 30;
          const sessionDurationMs = sessionDurationDays * 24 * 60 * 60 * 1000;
          if (lastActivity && (Date.now() - parseInt(lastActivity)) < sessionDurationMs) {
            setPasswordVerified(true);
            // Update activity on load
            localStorage.setItem('vantapulse_last_activity', Date.now().toString());
          } else {
            setPasswordVerified(false);
          }
          setLoading(false);
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            tokenStore.remove();
            setLoading(false);
            return;
          }
          if (attemptsLeft > 0) {
            setTimeout(() => tryFetchMe(attemptsLeft - 1), 1500);
          } else {
            setLoading(false);
          }
        });
      };

      tryFetchMe(3);
    } else {
      setLoading(false);
    }
  }, []);

  // Cursor glow + click effects
  useEffect(() => {
    const glow = document.createElement('div');
    glow.className = 'cursor-glow';
    document.body.appendChild(glow);

    const moveGlow = (e) => {
      glow.style.left = e.clientX + 'px';
      glow.style.top = e.clientY + 'px';
    };

    const handleClick = (e) => {
      // Spark at click position
      const spark = document.createElement('div');
      spark.className = 'click-spark';
      spark.style.left = e.clientX + 'px';
      spark.style.top = e.clientY + 'px';
      document.body.appendChild(spark);
      setTimeout(() => spark.remove(), 500);

    };

    document.addEventListener('mousemove', moveGlow);
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('mousemove', moveGlow);
      document.removeEventListener('click', handleClick);
      glow.remove();
    };
  }, []);

  return (
    <LanguageProvider>
      <AuthContext.Provider value={{ user, login, beginOAuthSession, logout, loading, profile, refreshProfile, passwordVerified, setPasswordVerified }}>
        <div className="min-h-screen bg-vanta-base">
          <ParticlesBg />
          <div className="noise-overlay"></div>
          <BrowserRouter>
            <AppRouter siteStatus={siteStatus} />
            {/* Événement du moment (Noël, Halloween…) choisi dans Panel admin > Site > Événements */}
            <SeasonalLayer season={siteStatus?.season} />
          </BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#14151C',
                border: '1px solid #2A2D3D',
                color: '#fff',
              },
            }}
          />
        </div>
      </AuthContext.Provider>
    </LanguageProvider>
  );
}

export default App;
